import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, Check, Lock, ExternalLink, Cloud } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

// Authentication Component
const SalesforceAuth = ({ onAuthComplete }) => {
  const [isOpen, setIsOpen] = useState(true);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const handleLogin = () => {
    setShowConfirmation(true);
    localStorage.setItem('sf_auth_token', 'mock_token');
    setTimeout(() => {
      setIsOpen(false);
      onAuthComplete();
    }, 3000);
  };

  return (
    <Dialog open={isOpen}>
      <DialogContent className="sm:max-w-md w-full mx-auto p-4">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">Connect to Salesforce</DialogTitle>
        </DialogHeader>

        {!showConfirmation ? (
          <div className="space-y-4 py-4">
            <div className="mx-auto w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
              <Lock className="w-6 h-6 text-blue-600" />
            </div>

            <div className="text-center">
              <h3 className="text-base font-medium text-gray-900">One-time Salesforce Setup</h3>
              <p className="mt-2 text-sm text-gray-500">
                You'll need to log in to Salesforce once to enable logging comments from Teams. This is a one-time setup - you won't need to do this again on this device.
              </p>
            </div>

            <div className="bg-blue-50 rounded-md p-3">
              <ul className="text-sm text-gray-600 space-y-2">
                <li className="flex items-start">
                  <span className="mr-2">1.</span>
                  <span>You'll be redirected to the Salesforce login page</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">2.</span>
                  <span>Log in with your Salesforce credentials</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">3.</span>
                  <span>Allow the Teams app to access your Salesforce account</span>
                </li>
              </ul>
            </div>

            <button
              onClick={handleLogin}
              className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white p-3 rounded-md hover:bg-blue-700 touch-manipulation"
            >
              <span>Continue to Salesforce</span>
              <ExternalLink className="w-4 h-4" />
            </button>

            <p className="text-xs text-center text-gray-500">
              Your credentials are securely handled by Salesforce. We only receive an access token for future operations.
            </p>
          </div>
        ) : (
          <div className="py-8 px-4">
            <div className="max-w-sm mx-auto bg-white rounded-lg shadow-lg border p-6">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-blue-500 rounded-lg flex items-center justify-center">
                  <Cloud className="w-10 h-10 text-white" />
                </div>
              </div>
              <h3 className="text-xl font-semibold text-center mb-4">Allow Access?</h3>
              <div className="space-y-4 mb-6">
                <p className="text-sm text-gray-600">
                  Teams is requesting access to:
                </p>
                <ul className="text-sm text-gray-700 space-y-2">
                  <li className="flex items-center">
                    <Check className="w-4 h-4 text-blue-500 mr-2" />
                    Access your basic information
                  </li>
                  <li className="flex items-center">
                    <Check className="w-4 h-4 text-blue-500 mr-2" />
                    Create and modify records
                  </li>
                  <li className="flex items-center">
                    <Check className="w-4 h-4 text-blue-500 mr-2" />
                    Access your data anytime
                  </li>
                </ul>
              </div>
            </div>
            <p className="text-center text-sm text-gray-600 mt-4">
              Completing authorization...
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Main Logging Component
const TeamsToSalesforce = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(true);
  const [selectedObject, setSelectedObject] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [comment, setComment] = useState('Financial aid applications typically involve submitting the Free Application for Federal Student Aid (FAFSA) and, in some cases, additional forms specific to the university.');
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [actionType, setActionType] = useState('comment'); // Default to comment for Cases

  const salesforceObjects = [
    { id: 'case', label: 'Case' },
    { id: 'account', label: 'Account' },
    { id: 'contact', label: 'Contact' },
    { id: 'opportunity', label: 'Opportunity' }
  ];

  const mockSearchResults = [
    { id: 1, name: 'Financial Aid Package Stuck', number: '00001234' },
    { id: 2, name: 'Scholarship Payment Delay', number: '00001235' },
    { id: 3, name: 'Tuition Bill Issue', number: '00001236' }
  ];

  const handleSubmit = () => {
    setShowSuccess(true);
    setTimeout(() => {
      setIsOpen(false);
      setShowSuccess(false);
      navigate('/');
    }, 2000);
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="sm:max-w-lg w-full mx-auto p-4">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">Log to Salesforce</DialogTitle>
          <span 
            onClick={() => setIsOpen(false)}
            className="absolute right-4 top-4 text-gray-500 cursor-pointer touch-manipulation"
          >
            <X size={20} />
          </span>
        </DialogHeader>

        {!showSuccess ? (
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium block mb-2">Select Object</label>
              <div className="grid grid-cols-2 gap-2">
                {salesforceObjects.map((obj) => (
                  <label 
                    key={obj.id} 
                    className={`flex items-center p-3 border rounded-md touch-manipulation cursor-pointer transition-colors ${
                      selectedObject === obj.id ? 'bg-blue-50 border-blue-200' : 'hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="radio"
                      name="salesforceObject"
                      value={obj.id}
                      checked={selectedObject === obj.id}
                      onChange={(e) => setSelectedObject(e.target.value)}
                      className="h-4 w-4 text-blue-600"
                    />
                    <span className="ml-2">{obj.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {selectedObject && (
              <>
                <div className="relative">
                  <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400 pointer-events-none" />
                  <input
                    type="text"
                    placeholder={`Search ${selectedObject}...`}
                    className="pl-10 pr-4 py-2 w-full border rounded-md touch-manipulation"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>

                <div className="border rounded-md max-h-40 overflow-y-auto">
                  {mockSearchResults.map((result) => (
                    <div
                      key={result.id}
                      className={`p-3 border-b last:border-b-0 touch-manipulation ${
                        selectedRecord?.id === result.id ? 'bg-blue-50' : ''
                      }`}
                      onClick={() => setSelectedRecord(result)}
                    >
                      <div className="flex justify-between items-center">
                        <span className="truncate">{result.name}</span>
                        <span className="text-sm text-gray-500 ml-2">#{result.number}</span>
                      </div>
                    </div>
                  ))}
                </div>

                {selectedObject === 'case' && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium block">Action Type</label>
                    <div className="flex gap-4 text-sm">
                      <label className="flex items-center touch-manipulation">
                        <input
                          type="radio"
                          name="actionType"
                          value="comment"
                          checked={actionType === 'comment'}
                          onChange={(e) => setActionType(e.target.value)}
                          className="mr-2 h-4 w-4"
                        />
                        Case Comment
                      </label>
                      <label className="flex items-center touch-manipulation">
                        <input
                          type="radio"
                          name="actionType"
                          value="task"
                          checked={actionType === 'task'}
                          onChange={(e) => setActionType(e.target.value)}
                          className="mr-2 h-4 w-4"
                        />
                        Create Task
                      </label>
                    </div>
                  </div>
                )}

                <div>
                  <label className="text-sm font-medium block mb-1">Comment</label>
                  <textarea
                    className="w-full px-3 py-2 border rounded-md h-24 resize-none"
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                  />
                </div>

                <div
                  onClick={handleSubmit}
                  className={`p-3 rounded-md text-center text-white touch-manipulation ${
                    selectedRecord
                      ? 'bg-blue-600 active:bg-blue-700'
                      : 'bg-gray-300'
                  }`}
                >
                  Log to Salesforce
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="py-6 text-center">
            <div className="mx-auto w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mb-3">
              <Check className="w-5 h-5 text-green-600" />
            </div>
            <h3 className="text-base font-medium text-gray-900">Logged to Salesforce</h3>
            <p className="mt-1 text-sm text-gray-500">
              View {actionType}:{' '}
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
              <a href="#" className="text-blue-600">
                #{selectedRecord?.number}
              </a>
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Main Component with Authentication Handling
const TeamsToSalesforceWithAuth = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const hasAuthToken = localStorage.getItem('sf_auth_token');
    if (hasAuthToken) {
      setIsAuthenticated(true);
    }
  }, []);

  if (!isAuthenticated) {
    return <SalesforceAuth onAuthComplete={() => setIsAuthenticated(true)} />;
  }

  return <TeamsToSalesforce />;
};

export default TeamsToSalesforceWithAuth; 